import { FieldTypeMetadata } from '../../data-capture/types';
import dayjs from 'dayjs';
import { camelCaseOrSnakeCaseToWords } from '../../data-capture/merge-review/ReviewHelperFunctions';
import {
  DynamicUiData,
  EntityFields,
  EntityWithFieldMetadata,
  EntityWithMetadata,
  FieldValue,
  FieldWithValue,
  FormValidationSchema,
  FormValuesType,
  UiEntityList,
  UiField,
  UiItem,
} from './types';

export function getInitialFormData(uiData: DynamicUiData | undefined, items: UiItem[]) {
  const formInitialData = {};
  const formValidationSchema = {};
  populateFormsDataSubFunction(uiData, items, formInitialData, formValidationSchema);
  return { initialValues: formInitialData, validate: formValidationSchema };
}

function populateFormsDataSubFunction(
  uiData: DynamicUiData | undefined,
  items: UiItem[],
  formInitialData: FormValuesType,
  validationSchema: FormValidationSchema,
) {
  items
    .filter(i => i.type !== 'SEPARATOR')
    .forEach(item => {
      if (item.type === 'UI_ENTITY' || item.type === 'FIELD_ROW') {
        populateFormsDataSubFunction(uiData, item.children, formInitialData, validationSchema);
      } else if (item.type === 'UI_ENTITY_SECTION_GROUP' || item.type === 'UI_GRID') {
        const sectionChildren = item.children.flatMap(c => (c.type === 'FIELD' ? c : c.children));
        populateFormsDataSubFunction(uiData, sectionChildren, formInitialData, validationSchema);
      }
      if (item.type === 'FIELD') {
        const entityOfField = uiData?.entities[item.entityType] as EntityWithMetadata;
        if (!formInitialData[item.entityType]) {
          formInitialData[item.entityType] = {};
          validationSchema[item.entityType] = {};
          if (entityOfField.entity.fields['id']) {
            (formInitialData[item.entityType] as FieldWithValue)['id'] =
              entityOfField.entity.fields['id'];
          }
        }

        const entityFieldValue = entityOfField.entity?.fields[item.entityField];

        const entityMetadata = entityOfField.entity?.metadata[item.entityField];

        (formInitialData[item.entityType] as FieldWithValue)[item.entityField] =
          getFieldInitialValue(entityFieldValue, entityMetadata);

        (validationSchema[item.entityType] as FormValidationSchema)[item.entityField] = value =>
          value || entityMetadata?.isOptional ? null : 'This field is required';
      } else if (item.type === 'ENTITY_LIST') {
        if (!formInitialData[item.entityType]) {
          formInitialData[item.entityType] = [];
        }

        formInitialData[item.entityType] = (
          uiData?.entities[item.entityType] as EntityWithMetadata[]
        ).map(entity => getRelevantInitialFormDataForEntityList(item, entity));
      }
    });
}

function getRelevantInitialFormDataForEntityList(
  uiEntityList: UiEntityList,
  entity: EntityWithMetadata,
) {
  return uiEntityList.children.reduce(
    (relevantFields, uiField) => {
      if (uiField.type === 'FIELD_ROW') {
        const innerFields = uiField.children.reduce((defaultInnerFields, uiInnerField) => {
          return {
            ...defaultInnerFields,
            [uiInnerField.entityField]: getFieldInitialValue(
              entity.entity.fields[uiInnerField.entityField],
              entity.entity.metadata[uiInnerField.entityField],
            ),
          };
        }, {} as FieldWithValue);
        return { ...relevantFields, ...innerFields };
      }

      return {
        ...relevantFields,
        [uiField.entityField]: getFieldInitialValue(
          entity.entity.fields[uiField.entityField],
          entity.entity.metadata[uiField.entityField],
        ),
      };
    },
    { id: entity.entity.fields['id'] } as FieldWithValue,
  );
}

function getFieldInitialValue(
  fieldValue: EntityFields | FieldValue | FieldValue[] | EntityFields[],
  metadata: FieldTypeMetadata,
) {
  if (!fieldValue && metadata?.type === 'LIST') {
    return [''];
  }

  if (metadata?.type === 'DATE' && typeof fieldValue === 'string') {
    return fieldValue ? dayjs(fieldValue).toDate() : null;
  }

  if (metadata?.type === 'BOOLEAN' && typeof fieldValue === 'boolean') {
    return fieldValue ? 'true' : 'false';
  }

  if (metadata?.type === 'MONEY' && fieldValue) {
    return parseFloat(fieldValue.toString());
  }

  return fieldValue;
}

export function getDynamicUiDataFromEntityWithFieldMetadata(
  entity: EntityWithFieldMetadata,
): DynamicUiData {
  const entityWrapper: EntityWithMetadata = {
    id: entity.fields['id'] as string,
    entityType: entity.entityType,
    entityLabel: camelCaseOrSnakeCaseToWords(entity.entityType),
    entity: entity,
  };

  const getLayout = (): UiItem[] => {
    const sortedLayout = Object.keys(entity.fields).map(
      (f): UiField => ({
        type: 'FIELD',
        span: 12,
        label: camelCaseOrSnakeCaseToWords(f),
        entityType: entity.entityType,
        entityField: f,
      }),
    );

    sortedLayout.sort((a, b) => {
      return (
        (entity.metadata[a.entityField]?.orderPosition || 1000) -
        (entity.metadata[b.entityField]?.orderPosition || 1000)
      );
    });
    return sortedLayout;
  };

  return {
    entities: {
      [entity.entityType]: entityWrapper,
    },
    ui: {
      layout: getLayout(),
      entitiesMetadata: {
        [entity.entityType]: entity.metadata,
      },
    },
  };
}
